import {
  AuthDivider,
  AuthFooterLink,
  AuthInput,
  AuthMessage,
  AuthPasswordInput,
  AuthSubmitButton,
  GoogleAuthButton,
  Select,
  SelectItem,
} from '@protoss/ui';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicAppConfig } from '../../contexts/PublicAppConfigContext';
import { getPreAuthConfig } from '../../lib/preAuthConfig';
import './auth-pages.css';

/**
 * Sign Up Page
 *
 * Supports Google OAuth (primary) and email/password (secondary).
 * Auth logic is delegated to useAuth() - no provider-specific code here.
 */

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

type FormState = 'idle' | 'loading' | 'error';

export function SignUpPage() {
  const {
    config: { productName },
  } = usePublicAppConfig();
  const allowedMethods = getPreAuthConfig().allowedMethods;
  const showGoogleAuth = allowedMethods.includes('google');
  const showPasswordAuth = allowedMethods.includes('password');
  const { signInWithGoogle, signUpWithPassword } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = React.useState<FormErrors>({});
  const [formState, setFormState] = React.useState<FormState>('idle');
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  // Validation
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Full name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    // Optional: Add stronger password requirements
    // if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    //   return 'Password must contain uppercase, lowercase, and number';
    // }
    return undefined;
  };

  const validateConfirmPassword = (
    password: string,
    confirmPassword: string
  ): string | undefined => {
    if (!confirmPassword) {
      return 'Please confirm your password';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword),
    };

    setErrors(newErrors);
    return !newErrors.name && !newErrors.email && !newErrors.password && !newErrors.confirmPassword;
  };

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Also clear confirm password error if user is typing in password field
    if (name === 'password' && errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }

    // Clear general error when user modifies form
    if (generalError) {
      setGeneralError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setGeneralError(null);

    if (!validateForm()) {
      return;
    }

    setFormState('loading');

    try {
      await signUpWithPassword(formData.email, formData.password, formData.name);
      navigate('/verify-email-sent', { replace: true });
    } catch (error) {
      setFormState('error');
      const msg =
        error instanceof Error ? error.message : 'Unable to create account. Please try again.';
      // Surface a friendlier message for the most common Firebase errors.
      if (msg === 'NO_WORKSPACE_ACCESS') {
        setGeneralError(
          'This workspace is invite-only. Contact your workspace admin to receive an invitation.'
        );
      } else if (msg.includes('email-already-in-use')) {
        setGeneralError('An account with this email already exists. Try signing in instead.');
      } else if (msg.includes('weak-password')) {
        setGeneralError('Your password is too weak. Please choose a stronger password.');
      } else {
        setGeneralError(msg);
      }
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setGeneralError(null);

    try {
      await signInWithGoogle();
      navigate('/', { replace: true });
    } catch (error) {
      setIsGoogleLoading(false);
      const message = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
      setGeneralError(
        message === 'NO_WORKSPACE_ACCESS'
          ? 'This workspace is invite-only. Contact your workspace admin to receive an invitation.'
          : 'Unable to connect with Google. Please try again.'
      );
    }
  };

  // DEV-only workspace switcher state
  const [searchParams] = useSearchParams();
  const [devWorkspaces, setDevWorkspaces] = React.useState<{ slug: string; name: string }[]>([]);
  const devWorkspaceSlug = import.meta.env.DEV ? (searchParams.get('workspace') ?? '') : '';

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    fetch('/api/auth/workspaces')
      .then((r) => r.json())
      .then(({ workspaces }: { workspaces: { slug: string; name: string }[] }) =>
        setDevWorkspaces(workspaces)
      )
      .catch(() => {});
  }, []);

  const handleDevWorkspaceChange = (slug: string) => {
    const resolved = slug === '_default' ? '' : slug;
    const next = new URLSearchParams(window.location.search);
    if (resolved) {
      next.set('workspace', resolved);
    } else {
      next.delete('workspace');
    }
    window.location.assign(`/signup?${next.toString()}`);
  };

  const isLoading = formState === 'loading';

  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          title={`Create your ${productName} account`}
          subtitle="Start managing operational context in one place"
        />

        <div className="auth-form-container">
          {/* Google OAuth */}
          {showGoogleAuth && (
            <>
              <GoogleAuthButton onClick={handleGoogleSignUp} isLoading={isGoogleLoading}>
                Continue with Google
              </GoogleAuthButton>

              {showPasswordAuth && <AuthDivider />}
            </>
          )}

          {/* General error message */}
          {generalError && <AuthMessage variant="error">{generalError}</AuthMessage>}

          {/* Sign Up Form */}
          {showPasswordAuth && (
            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <AuthInput
                type="text"
                name="name"
                label="Full Name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                autoComplete="name"
                disabled={isLoading}
              />

              <AuthInput
                type="email"
                name="email"
                label="Work Email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                autoComplete="email"
                disabled={isLoading}
              />

              <AuthPasswordInput
                name="password"
                label="Password"
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                helperText={!errors.password ? 'Must be at least 8 characters' : undefined}
                autoComplete="new-password"
                disabled={isLoading}
              />

              <AuthPasswordInput
                name="confirmPassword"
                label="Confirm Password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                error={errors.confirmPassword}
                autoComplete="new-password"
                disabled={isLoading}
              />

              <AuthSubmitButton isLoading={isLoading}>Create Account</AuthSubmitButton>
            </form>
          )}
        </div>

        {import.meta.env.DEV && devWorkspaces.length > 1 && (
          <div className="auth-dev-workspace-picker">
            <span className="auth-dev-workspace-picker__label">Dev workspace</span>
            <Select
              value={devWorkspaceSlug || '_default'}
              onValueChange={handleDevWorkspaceChange}
              size="comfortable"
            >
              <SelectItem value="_default">default (first)</SelectItem>
              {devWorkspaces.map((ws) => (
                <SelectItem key={ws.slug} value={ws.slug}>
                  {ws.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        )}

        <div className="auth-footer-text">
          Already have an account? <AuthFooterLink href="/signin">Sign in</AuthFooterLink>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
